import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import AuthModal from "./AuthModal";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const { openCart, cartItems } = useCart();
  const { user, member, signOut } = useAuth();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? "bg-white shadow-md"
            : "bg-transparent"
        }`}
      >
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg">
                <i className="ri-flower-line text-2xl text-white"></i>
              </div>
              <span
                className={`text-xl font-bold hidden md:block ${
                  isScrolled || !isHomePage ? "text-gray-900" : "text-white"
                }`}
              >
                Monceau Fleurs
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              <a
                href="#about"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                關於我們
              </a>
              <a
                href="#products"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                商品系列
              </a>
              <a
                href="#services"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                服務項目
              </a>
              <Link
                to="/seasonal-booking"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                節日預約
              </Link>
              <a
                href="#blog"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                花藝知識
              </a>
              <a
                href="#contact"
                className={`font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:text-teal-600"
                    : "text-white hover:text-teal-200"
                }`}
              >
                聯絡我們
              </a>
            </div>

            <div className="flex items-center gap-4">
              {user && member ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      isScrolled || !isHomePage
                        ? "text-gray-700 hover:bg-gray-100"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    <i className="ri-user-line text-xl"></i>
                    <span className="hidden md:inline">{member.name}</span>
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg py-2 border border-gray-100">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="font-semibold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                        <div className="mt-2 flex items-center gap-2 text-sm">
                          <i className="ri-coin-line text-teal-600"></i>
                          <span className="text-gray-700">可用點數：</span>
                          <span className="font-semibold text-teal-600">{member.points}</span>
                        </div>
                      </div>
                      <Link
                        to="/member"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <i className="ri-user-settings-line text-gray-600"></i>
                        <span className="text-gray-700">會員中心</span>
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <i className="ri-logout-box-line text-gray-600"></i>
                        <span className="text-gray-700">登出</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    isScrolled || !isHomePage
                      ? "text-gray-700 hover:bg-gray-100"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  <i className="ri-user-line text-xl"></i>
                  <span className="hidden md:inline">會員登入</span>
                </button>
              )}

              <button
                onClick={openCart}
                className={`relative p-2 rounded-lg transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:bg-gray-100"
                    : "text-white hover:bg-white/10"
                }`}
              >
                <i className="ri-shopping-cart-line text-2xl"></i>
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-teal-600 text-white text-xs font-bold rounded-full">
                    {cartItemCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isScrolled || !isHomePage
                    ? "text-gray-700 hover:bg-gray-100"
                    : "text-white hover:bg-white/10"
                }`}
              >
                <i
                  className={`${
                    isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"
                  } text-2xl`}
                ></i>
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <a
                  href="#about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  關於我們
                </a>
                <a
                  href="#products"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  商品系列
                </a>
                <a
                  href="#services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  服務項目
                </a>
                <Link
                  to="/seasonal-booking"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  節日預約
                </Link>
                <a
                  href="#blog"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  花藝知識
                </a>
                <a
                  href="#contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-600 font-medium"
                >
                  聯絡我們
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
}
